import React, { useCallback, useEffect, useState, FC, ReactElement } from 'react';
import { useLocation } from 'react-router-dom';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Paper, Button } from '@mui/material';
import {
  ArrowLeft,
  ArrowRight,
  ChevronDown,
  Cart,
  CreditCard,
  JournalText,
  Person,
  PersonBadge,
  Building,
  Book,
  Grid as DashboardSquares
} from 'react-bootstrap-icons';

import { reduxActions, useDispatch } from 'store';
import { Link } from 'components/controls';
import { ClipboardIcon } from 'assets/icons';

import styles from 'theme/components/wise/WiseDashboardMenu/WiseDashboardMenu.module.scss';

interface IRoute {
  label: string;
  path: string;
}

interface IAccessRoutes {
  orders: IRoute[];
  carts: IRoute[];
  payments: IRoute[];
  offers: IRoute[];
  myData: IRoute[];
  clients: IRoute[];
  catalog: IRoute[];
  search: IRoute[];
  marketing: IRoute[];
  users: IRoute[];
  documents: IRoute[];
  myCompany: IRoute[];
  cms: IRoute[];
  gpsr: IRoute[];
  configuration: IRoute[];
  adminApi: IRoute[];
  system: IRoute[];
}

interface IMenuItem {
  label: string;
  icon: ReactElement;
  items?: IRoute[];
  path?: string;
}

export interface IWiseDashboardMenuProps {
  accessRoutes: IAccessRoutes;
  isCondensed?: boolean;
  onToggleCondensed?: () => void;
}

const WiseDashboardMenu: FC<IWiseDashboardMenuProps> = ({
  accessRoutes,
  isCondensed = false,
  onToggleCondensed
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { pathname } = useLocation();

  const [openedParentItemIndexes, setOpenedParentItemIndexes] = useState<number[]>([]);

  const menuItems: IMenuItem[] = [
    {
      label: 'Dashboard',
      icon: <DashboardSquares />,
      path: '/dashboard'
    },
    {
      label: 'Zamówienia',
      icon: <JournalText />,
      items: accessRoutes.orders
    },
    {
      label: 'Koszyki',
      icon: <Cart />,
      items: accessRoutes.carts
    },
    {
      label: 'Płatności',
      icon: <CreditCard />,
      items: accessRoutes.payments
    },
    {
      label: 'Oferty',
      icon: <ClipboardIcon />,
      items: accessRoutes.offers
    },
    {
      label: 'Dokumenty',
      icon: <Book />,
      items: accessRoutes.documents
    },
    {
      label: 'Klienci',
      icon: <PersonBadge />,
      items: accessRoutes.clients
    },
    {
      label: 'Użytkownicy',
      icon: <Person />,
      items: accessRoutes.users
    },
    {
      label: 'Moje dane',
      icon: <Person />,
      items: accessRoutes.myData
    },
    {
      label: 'Moja firma',
      icon: <Building />,
      items: accessRoutes.myCompany
    }
  ];

  const isCurrentPath = useCallback(
    (testedPath?: string) =>
      testedPath ? new RegExp(`^/[^\\/]+${testedPath}$`).test(pathname) : false,
    [pathname]
  );

  useEffect(() => {
    const blockItemIndex = menuItems.findIndex((menuItem) =>
      menuItem.items?.some((subItem) => isCurrentPath(subItem.path))
    );
    if (blockItemIndex >= 0) {
      setOpenedParentItemIndexes((prevState) =>
        prevState.includes(blockItemIndex) ? prevState : [...prevState, blockItemIndex]
      );
    }
  }, [pathname]);

  // przy zwinięciu sidebaru chowamy też wszystkie sub-itemy
  useEffect(() => {
    if (isCondensed) {
      setOpenedParentItemIndexes([]);
    }
  }, [isCondensed]);

  const toggleItem = (index: number) => {
    setOpenedParentItemIndexes((prevState) =>
      prevState.includes(index) ? prevState.filter((item) => item !== index) : [...prevState, index]
    );
  };

  const renderItem = (parentItem: IMenuItem, index: number) => {
    if (parentItem?.items && parentItem.items.length === 0) {
      return null;
    }

    const hasActiveSub = parentItem.items?.some((s) => isCurrentPath(s.path));
    const isExpanded = openedParentItemIndexes.includes(index) && !isCondensed;

    return (
      <div className={styles.menuItem} key={index}>
        {!parentItem.items && parentItem.path ? (
          <Link
            to={parentItem.path}
            onClick={() => dispatch(reduxActions.setIsMobileMenu(false))}
            className={classnames(styles.link, {
              [styles.active]: isCurrentPath(parentItem.path),
              [styles.condensed]: isCondensed
            })}
            title={isCondensed ? t(parentItem.label) : undefined}>
            <span className={styles.iconWrapper}>{parentItem.icon}</span>
            {!isCondensed && <span className={styles.linkLabel}>{t(parentItem.label)}</span>}
          </Link>
        ) : (
          <button
            type="button"
            className={classnames(styles.link, styles.parentLink, {
              [styles.activeParent]: hasActiveSub,
              [styles.open]: isExpanded,
              [styles.condensed]: isCondensed
            })}
            onClick={() => !isCondensed && toggleItem(index)}
            title={isCondensed ? t(parentItem.label) : undefined}>
            <span className={styles.iconWrapper}>{parentItem.icon}</span>
            {!isCondensed && (
              <>
                <span className={styles.linkLabel}>{t(parentItem.label)}</span>
                <ChevronDown className={styles.arrow} />
              </>
            )}
          </button>
        )}

        {isExpanded && (
          <div className={styles.subItemsWrapper}>
            {(parentItem.items || []).map((subItem, subItemIndex) => (
              <Link
                key={subItemIndex}
                to={subItem.path}
                onClick={() => dispatch(reduxActions.setIsMobileMenu(false))}
                className={classnames(styles.subItem, {
                  [styles.current]: isCurrentPath(subItem.path)
                })}>
                {t(subItem.label)}
              </Link>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <Paper
      className={classnames(
        styles.componentWrapper,
        'StylePath-Components-Wise-WiseDashboardMenu',
        { [styles.condensed]: isCondensed }
      )}>
      <div className={styles.menuItems}>{menuItems.map((item, idx) => renderItem(item, idx))}</div>
      {onToggleCondensed && (
        <div
          className={classnames(styles.bottomSection, {
            [styles.bottomSectionCondensed]: isCondensed
          })}>
          <Button
            variant="text"
            className={styles.collapseButton}
            startIcon={isCondensed ? <ArrowRight /> : <ArrowLeft />}
            onClick={onToggleCondensed}>
            {isCondensed ? '' : t('Zwiń panel')}
          </Button>
        </div>
      )}
    </Paper>
  );
};

export default WiseDashboardMenu;
